/*
 * Colour contrast as WCAG 2.2 measures it (relative luminance, then (L1 + 0.05) / (L2 + 0.05)), for the app's theme
 * guard rail: text and buttons keep 4.5:1 (CLAUDE.md §4). Colours are #rrggbb; nothing else is stored.
 */

/** #rrggbb, lower case. A theme stores nothing else (no alpha, no names), so contrast is always measurable. */
export const HEX = /^#[0-9a-f]{6}$/;

/** "#ABC", "abcdef" or "#AbCdEf" as #abcdef, or null when it isn't a colour. */
export function normaliseHex(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  let s = raw.trim().toLowerCase();
  if (!s.startsWith('#')) s = `#${s}`;
  if (/^#[0-9a-f]{3}$/.test(s)) s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  return HEX.test(s) ? s : null;
}

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance of #rrggbb, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

/** The contrast ratio of two colours, 1 to 21, either way round. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a), lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** A ratio as people read it: 4.48 → "4.4:1" (rounded down, so a pass never reads as a fail or the other way). */
export const ratioText = (r: number): string => `${(Math.floor(r * 10) / 10).toFixed(1)}:1`;

/** The text guard rail (WCAG AA for normal text). */
export const MIN_CONTRAST = 4.5;

/** "rgb(r, g, b)", which Apple Wallet's pass.json takes. */
export function hexToRgb(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgb(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255})`;
}
